"use client";

import * as React from "react";
import { Spinner } from "@phosphor-icons/react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const LOGOUT_ENDPOINT = "/api/auth/signout";

type LogoutFormProps = {
  className?: string;
  children: React.ReactNode;
};

type LogoutPendingContextValue = {
  pending: boolean;
  beginPending: () => void;
};

const LogoutPendingContext = React.createContext<LogoutPendingContextValue | null>(
  null
);

/**
 * Wraps the logout form so child submit buttons can show pending feedback
 * (spinner + disabled) once native submit starts. Pending state must not be
 * set on button click — disabling the submit control before submit fires
 * prevents the browser from POSTing the form.
 */
export function LogoutForm({
  className,
  children,
}: LogoutFormProps): React.ReactElement {
  const [pending, setPending] = React.useState(false);
  const beginPending = React.useCallback(() => setPending(true), []);

  const handleSubmit = React.useCallback(() => {
    beginPending();
  }, [beginPending]);

  return (
    <LogoutPendingContext.Provider value={{ pending, beginPending }}>
      <form
        action={LOGOUT_ENDPOINT}
        method="post"
        onSubmit={handleSubmit}
        className={className}
      >
        {children}
      </form>
    </LogoutPendingContext.Provider>
  );
}

function useLogoutPending(): LogoutPendingContextValue {
  const ctx = React.useContext(LogoutPendingContext);
  if (!ctx) {
    throw new Error(
      "LogoutSubmitButton/LogoutSubmitCtaButton must be rendered inside <LogoutForm>"
    );
  }
  return ctx;
}

type LogoutBaseProps = {
  icon?: React.ReactNode;
  pendingLabel?: string;
  children: React.ReactNode;
};

/**
 * Plain <button> submit for use inside menu items where unstyled markup is
 * needed (e.g. DropdownMenuItem asChild).
 */
export const LogoutSubmitButton = React.forwardRef<
  HTMLButtonElement,
  LogoutBaseProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type" | "disabled">
>(({ icon, pendingLabel, children, className, ...rest }, ref) => {
  const { pending } = useLogoutPending();
  return (
    <button
      ref={ref}
      type="submit"
      disabled={pending}
      aria-busy={pending || undefined}
      className={cn("disabled:cursor-not-allowed disabled:opacity-70", className)}
      {...rest}
    >
      {pending ? (
        <Spinner className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        icon
      )}
      {pending ? (pendingLabel ?? "Logging out…") : children}
    </button>
  );
});
LogoutSubmitButton.displayName = "LogoutSubmitButton";

/**
 * Styled <Button> submit for use as a standalone logout CTA.
 */
export function LogoutSubmitCtaButton({
  icon,
  pendingLabel,
  children,
  className,
  variant = "destructive",
  onClick,
  ...rest
}: LogoutBaseProps & Omit<ButtonProps, "type" | "disabled">): React.ReactElement {
  const { pending } = useLogoutPending();
  return (
    <Button
      type="submit"
      disabled={pending}
      aria-busy={pending || undefined}
      variant={variant}
      className={className}
      onClick={onClick}
      {...rest}
    >
      {pending ? (
        <Spinner className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        icon
      )}
      {pending ? (pendingLabel ?? "Logging out…") : children}
    </Button>
  );
}
